import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import CheckIcon from "@material-ui/icons/Check";
import FilterListIcon from "@material-ui/icons/FilterList";
import SearchIcon from "@material-ui/icons/Search";

import { filtrarOpcoesPorTexto } from "./gruposProdutos";

// Painel: altura máxima (com muito grupo, ocupar a tela inteira seria pior que rolar) e as folgas
// usadas para decidir se ele cabe abaixo do botão. Ver `medirEspaco`.
const ALTURA_MAX_PAINEL = 360;
const DISTANCIA_BOTAO = 6;
const FOLGA_JANELA = 12;
const ALTURA_MIN_PAINEL = 140;

const useStyles = makeStyles(() => ({
  barra: {
    background: "#fff",
    padding: "4px 10px",
    fontSize: 12,
    color: "#6b6b6b",
    borderBottom: "1px solid #eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  // Ancora o painel, que é absoluto para não empurrar a tabela de produtos.
  wrapperFiltro: {
    position: "relative",
  },
  botaoFiltro: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    background: "#f5f5f5",
    border: "1px solid #ddd",
    borderRadius: 14,
    padding: "3px 10px",
    fontSize: 12,
    color: "#4b4b4b",
    cursor: "pointer",
    "&:hover": {
      background: "#ececec",
    },
  },
  badge: {
    background: "#dd0000",
    color: "#fff",
    borderRadius: 9,
    minWidth: 18,
    height: 18,
    lineHeight: "18px",
    padding: "0 5px",
    fontSize: 11,
    fontWeight: "bold",
    textAlign: "center",
  },
  // O lado (abaixo/acima do botão) e a altura máxima são calculados na abertura — o `estoqueBox` é
  // ancorado no rodapé e cresce para cima, então com poucos produtos a barra fica quase no fim da
  // janela e um painel fixo para baixo passava do rodapé, levando junto o "Ok" e o "Limpar".
  painel: {
    position: "absolute",
    right: 0,
    width: 280,
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 6,
    boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
    zIndex: 1200,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tituloPainel: {
    padding: "8px 10px",
    borderBottom: "1px solid #eee",
    fontSize: 13,
    fontWeight: "bold",
    color: "#4b4b4b",
  },
  busca: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 10px",
    borderBottom: "1px solid #eee",
    color: "#9b9b9b",
  },
  campoBusca: {
    flex: 1,
    minWidth: 0,
    border: "none",
    outline: "none",
    background: "transparent",
    fontSize: 13,
    color: "#4b4b4b",
    fontFamily: "inherit",
  },
  // Encolhe dentro da altura do painel: o cabeçalho, a busca e as ações ficam sempre visíveis, e o
  // que sobra é rolagem daqui. `minHeight: 0` é o que permite o flex item encolher.
  lista: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
    padding: "4px 0",
  },
  itemGrupo: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "5px 10px",
    fontSize: 13,
    color: "#4b4b4b",
    cursor: "pointer",
    "&:hover": {
      background: "#f5f5f5",
    },
  },
  itemFilho: {
    paddingLeft: 30,
    fontSize: 12,
  },
  itemImplicito: {
    color: "#9b9b9b",
    cursor: "default",
  },
  nomeGrupoPai: {
    fontWeight: "bold",
  },
  semResultado: {
    padding: "10px",
    fontSize: 12,
    color: "#9b9b9b",
    textAlign: "center",
  },
  box: {
    width: 18,
    height: 18,
    minWidth: 18,
    border: "2px solid #ccc",
    borderRadius: 5,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s ease",
  },
  boxMarcado: {
    backgroundColor: "#4CAF50",
    borderColor: "#4CAF50",
  },
  checkIcon: {
    color: "#fff",
    fontSize: 14,
    transform: "scale(0)",
    transition: "transform 0.2s ease",
  },
  checkIconVisivel: {
    transform: "scale(1)",
  },
  acoes: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "8px 10px",
    borderTop: "1px solid #eee",
  },
  botaoLimpar: {
    background: "none",
    border: "none",
    color: "#6b6b6b",
    fontSize: 12,
    cursor: "pointer",
    textDecoration: "underline",
  },
  botaoOk: {
    background: "#4CAF50",
    border: "none",
    borderRadius: 14,
    color: "#fff",
    fontSize: 12,
    fontWeight: "bold",
    padding: "4px 18px",
    cursor: "pointer",
  },
}));

/**
 * Linha acima da tabela de produtos: contagem à esquerda, filtro por grupo à direita.
 *
 * O painel trabalha num rascunho — a seleção só vale depois do "Ok", como pedido na tarefa. Fechar
 * clicando fora descarta o rascunho e mantém o que já estava aplicado.
 *
 * Sem grupos (ERP antigo, que não manda `grupoId`) o botão não aparece e a linha volta a ser só a
 * contagem — a tela continua funcionando exatamente como antes.
 */
const BarraProdutosFiltro = ({
  total,
  totalCarregado,
  max,
  grupos,
  gruposSelecionados,
  onAplicarGrupos,
}) => {
  const classes = useStyles();
  const [painelAberto, setPainelAberto] = useState(false);
  const [rascunho, setRascunho] = useState([]);
  const [busca, setBusca] = useState("");
  const [posicao, setPosicao] = useState({ paraCima: false, altura: ALTURA_MAX_PAINEL });
  const wrapperRef = useRef(null);

  const opcoes = useMemo(() => (Array.isArray(grupos) ? grupos : []), [grupos]);
  const aplicados = useMemo(
    () => (Array.isArray(gruposSelecionados) ? gruposSelecionados : []),
    [gruposSelecionados]
  );

  // Só o que a busca deixa passar. A lista completa continua sendo a `opcoes`: é dela que saem os
  // filhos de um pai marcado e o estado dos checkboxes, que não podem depender do texto digitado.
  const visiveis = useMemo(() => filtrarOpcoesPorTexto(opcoes, busca), [opcoes, busca]);

  // Reabrir o painel sempre parte do que está aplicado — e uma seleção aplicada por fora (reset ao
  // trocar de conversa, por exemplo) não pode deixar rascunho velho para trás. A busca também zera:
  // reabrir num filtro de texto de outra vez esconderia grupos sem explicação.
  useEffect(() => {
    if (painelAberto) {
      setRascunho(aplicados);
      setBusca("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painelAberto, gruposSelecionados]);

  // Abre para baixo quando o painel inteiro cabe ali; senão vira para cima, para o lado que tiver
  // mais espaço. `total` entra nas deps porque filtrar a tabela muda a altura dela e, com isso, a
  // posição da barra na janela.
  const medirEspaco = useCallback(() => {
    const elemento = wrapperRef.current;
    if (!elemento) return;

    const retangulo = elemento.getBoundingClientRect();
    const abaixo = window.innerHeight - retangulo.bottom - DISTANCIA_BOTAO - FOLGA_JANELA;
    const acima = retangulo.top - DISTANCIA_BOTAO - FOLGA_JANELA;
    const paraCima = abaixo < ALTURA_MAX_PAINEL && acima > abaixo;
    const disponivel = paraCima ? acima : abaixo;

    setPosicao({
      paraCima,
      altura: Math.max(ALTURA_MIN_PAINEL, Math.min(ALTURA_MAX_PAINEL, disponivel)),
    });
  }, []);

  useLayoutEffect(() => {
    if (!painelAberto) return undefined;
    medirEspaco();
    window.addEventListener("resize", medirEspaco);
    return () => window.removeEventListener("resize", medirEspaco);
  }, [painelAberto, total, medirEspaco]);

  const paiNoRascunho = useCallback(
    (opcao) => opcao.paiId != null && rascunho.includes(opcao.paiId),
    [rascunho]
  );

  // Marcar o pai já traz os filhos, então os filhos saem do rascunho: o número do badge conta
  // filtros aplicados, e um filho junto do pai seria o mesmo filtro contado duas vezes.
  const alternarGrupo = useCallback((opcao) => {
    setRascunho((anterior) => {
      if (anterior.includes(opcao.id)) {
        return anterior.filter((id) => id !== opcao.id);
      }
      const filhos = opcoes.filter((o) => o.paiId === opcao.id).map((o) => o.id);
      return [...anterior.filter((id) => !filhos.includes(id)), opcao.id];
    });
  }, [opcoes]);

  const confirmar = useCallback(() => {
    if (typeof onAplicarGrupos === "function") onAplicarGrupos(rascunho);
    setPainelAberto(false);
  }, [onAplicarGrupos, rascunho]);

  // Enquanto nada carregou a barra não existe — antes daqui não havia linha nenhuma nesse momento,
  // e um "0 produtos" piscando na abertura seria pior. Note que o guard é sobre a lista CARREGADA:
  // com filtro aplicado e resultado vazio a barra fica, senão não haveria como limpar o filtro.
  if (!(totalCarregado > 0)) return null;

  const textoContagem = total > max
    ? `Mostrando ${max} de ${total} produtos`
    : `${total} ${total === 1 ? "produto" : "produtos"}`;

  const estiloPainel = {
    maxHeight: posicao.altura,
    ...(posicao.paraCima
      ? { bottom: `calc(100% + ${DISTANCIA_BOTAO}px)` }
      : { top: `calc(100% + ${DISTANCIA_BOTAO}px)` }),
  };

  return (
    <div className={classes.barra}>
      <span>{textoContagem}</span>

      {opcoes.length > 0 && (
        <div className={classes.wrapperFiltro} ref={wrapperRef}>
          <button
            type="button"
            className={classes.botaoFiltro}
            onClick={() => setPainelAberto((prev) => !prev)}
          >
            <FilterListIcon style={{ fontSize: 16 }} />
            Grupo
            {aplicados.length > 0 && (
              <span className={classes.badge}>{aplicados.length}</span>
            )}
          </button>

          {painelAberto && (
            <ClickAwayListener onClickAway={() => setPainelAberto(false)}>
              <div className={classes.painel} style={estiloPainel}>
                <div className={classes.tituloPainel}>Filtrar por grupo</div>
                <div className={classes.busca}>
                  <SearchIcon style={{ fontSize: 18 }} />
                  <input
                    className={classes.campoBusca}
                    type="text"
                    autoFocus
                    value={busca}
                    placeholder="Buscar grupo"
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                <div className={classes.lista}>
                  {visiveis.length === 0 && (
                    <div className={classes.semResultado}>Nenhum grupo encontrado</div>
                  )}
                  {visiveis.map((opcao) => {
                    const implicito = paiNoRascunho(opcao);
                    const marcado = implicito || rascunho.includes(opcao.id);
                    return (
                      <div
                        key={opcao.id}
                        className={[
                          classes.itemGrupo,
                          opcao.nivel > 0 ? classes.itemFilho : "",
                          implicito ? classes.itemImplicito : "",
                        ].join(" ")}
                        onClick={implicito ? undefined : () => alternarGrupo(opcao)}
                        title={implicito ? "Já incluído pelo grupo pai" : undefined}
                      >
                        <div className={`${classes.box} ${marcado ? classes.boxMarcado : ""}`}>
                          <CheckIcon
                            className={`${classes.checkIcon} ${marcado ? classes.checkIconVisivel : ""}`}
                          />
                        </div>
                        <span className={opcao.nivel === 0 ? classes.nomeGrupoPai : undefined}>
                          {opcao.nome}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className={classes.acoes}>
                  <button
                    type="button"
                    className={classes.botaoLimpar}
                    onClick={() => setRascunho([])}
                  >
                    Limpar
                  </button>
                  <button type="button" className={classes.botaoOk} onClick={confirmar}>
                    Ok
                  </button>
                </div>
              </div>
            </ClickAwayListener>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(BarraProdutosFiltro);
